import React from "react";
import { Typography } from "@mui/material";
import { CurrencyInput, Select, TextField } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import { useMask } from "@react-input/mask";
import { useTranslation } from "react-i18next";
import { businessInformationValidationSchema } from "../../validations/businessInformation";
import BusinessStructure from "enums/BusinessStructure";
import useCompanySettings from "../../hooks/useCompanySettings";
import { OnboardingProps } from "../../pages/onboarding/OnboardCompany";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { useAddressData } from "hooks/useAddressData";
import {
  ConfigureAddressValidationType,
  incorporationStateOptions,
} from "utils/addressFormat";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import ConfigureAddress from "components/common/ConfigureAddress";
import PatriotActNotice from "components/common/PatriotActNotice";
import { useBrandingContext } from "context/BrandingContext";
import StepHeader from "./StepHeader";
import StepFooter from "./StepFooter";

// The form stores the enum key, which is what the validation schema tests.
const SOLE_PROPRIETORSHIP: keyof typeof BusinessStructure =
  "SOLE_PROPRIETORSHIP";

const businessStructureOptions = (
  Object.keys(BusinessStructure) as (keyof typeof BusinessStructure)[]
).map((key) => ({ id: key, label: BusinessStructure[key] }));

const incorporationOptions = incorporationStateOptions.map((option) => ({
  id: option.value,
  label: option.label,
}));

const BusinessInformation: React.FC<OnboardingProps> = (props) => {
  const { onboardingInfo, handleNext } = props;
  const addressId = onboardingInfo?.addressProfile?.id;
  const { t } = useTranslation();
  const { platform } = useBrandingContext();
  const platformName = platform?.name || "";

  const { formikAddress, specificLoadingStates, handleSaveAddress } =
    useAddressData({
      initialValues: onboardingInfo?.addressProfile,
      addressId: addressId,
      loadDefaultAddress: !addressId,
      type: addressId
        ? ConfigureAddressValidationType.VALIDATE
        : ConfigureAddressValidationType.CREATE,
    });

  const {
    companyProfile,
    specificLoadingStates: { profile: companyLoading },
  } = useCompanySettings({
    initialValues: onboardingInfo?.companyProfile,
    formikValidation: businessInformationValidationSchema,
  });

  const einRef = useMask({
    mask: "__-_______",
    replacement: { _: /\d/ },
  });

  const phoneMaskRef = useMask({
    mask: "___-___-____",
    replacement: { _: /\d/ },
  });

  const {
    validateAndTouch: validateCompany,
    handleFormikError: handleCompanyError,
    handleFormikHelperText: handleCompanyHelperText,
  } = useFormikHelpers(companyProfile);

  const { validateAndTouch: validateAddressTwo } =
    useFormikHelpers(formikAddress);

  async function handleSubmit() {
    const isCompanyValid = await validateCompany();
    const isAddressValid = await validateAddressTwo();

    if (!isCompanyValid || !isAddressValid) return;

    await companyProfile.submitForm();
    const createdId = await handleSaveAddress();
    if (!createdId) return;

    handleNext({
      companyProfile: companyProfile.values,
      addressProfile: { ...formikAddress.values, id: createdId },
    });
  }

  function handleWebsiteBlur(
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    companyProfile.handleBlur(e);
    const value = (companyProfile.values.websiteURL || "").trim();
    if (value && !/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
      companyProfile.setFieldValue("websiteURL", `https://${value}`);
    }
  }

  const loading =
    companyLoading ||
    specificLoadingStates.creatingAddress ||
    specificLoadingStates.updatingAddress ||
    specificLoadingStates.populatingAddress ||
    specificLoadingStates.loadingDefaultAddress;

  return (
    <>
      <StepHeader
        title={t("Business information")}
        subtitle={t("BusinessInformationDescription", { platformName })}
      >
        <PatriotActNotice />
      </StepHeader>

      <Stack
        component="form"
        gap="var(--space-5)"
        sx={{ marginTop: "var(--space-7)" }}
      >
        <TextField
          fullWidth
          required
          label={t("Business Legal Name")}
          name="businessLegalName"
          value={companyProfile.values.businessLegalName}
          onChange={companyProfile.handleChange}
          onBlur={companyProfile.handleBlur}
          error={handleCompanyError("businessLegalName")}
          helperText={handleCompanyHelperText("businessLegalName")}
        />

        <TextField
          fullWidth
          label={t("Business DBA (Doing Business As)")}
          name="businessDBA"
          value={companyProfile.values.businessDBA}
          onChange={companyProfile.handleChange}
        />

        <Select
          fullWidth
          required
          label={t("Business Type")}
          options={businessStructureOptions}
          value={companyProfile.values.businessStructure || null}
          onChange={(id) =>
            companyProfile.setFieldValue("businessStructure", id ?? "")
          }
          errorText={handleCompanyHelperText("businessStructure") || undefined}
        />

        {companyProfile.values.businessStructure !== SOLE_PROPRIETORSHIP && (
          <TextField
            fullWidth
            required
            label={t("Business EIN")}
            ref={einRef}
            name="businessEIN"
            value={companyProfile.values.businessEIN}
            onChange={companyProfile.handleChange}
            onBlur={companyProfile.handleBlur}
            error={handleCompanyError("businessEIN")}
            helperText={handleCompanyHelperText("businessEIN")}
          />
        )}

        <TextField
          fullWidth
          multiline
          minRows={3}
          label={t("Business Description")}
          name="description"
          value={companyProfile.values.description}
          onChange={companyProfile.handleChange}
          error={handleCompanyError("description")}
          helperText={handleCompanyHelperText("description")}
        />

        <TextField
          fullWidth
          label={t("Business Website")}
          name="websiteURL"
          value={companyProfile.values.websiteURL}
          onChange={companyProfile.handleChange}
          onBlur={handleWebsiteBlur}
          error={handleCompanyError("websiteURL")}
          helperText={handleCompanyHelperText("websiteURL")}
        />

        <TextField
          fullWidth
          required
          ref={phoneMaskRef}
          label={t("Business Phone Number")}
          name="phoneNumber"
          placeholder="123-456-7890"
          leading="+1"
          value={companyProfile.values.phoneNumber}
          onChange={companyProfile.handleChange}
          onBlur={companyProfile.handleBlur}
          error={handleCompanyError("phoneNumber")}
          helperText={handleCompanyHelperText("phoneNumber")}
        />

        <Select
          fullWidth
          required
          label={t("State of incorporation")}
          options={incorporationOptions}
          value={companyProfile.values.businessIncorporationState || null}
          onChange={(id) =>
            companyProfile.setFieldValue("businessIncorporationState", id ?? "")
          }
          errorText={
            handleCompanyHelperText("businessIncorporationState") || undefined
          }
        />

        <CurrencyInput
          fullWidth
          required
          label={t("Annual Revenue")}
          name="annualRevenue"
          value={companyProfile.values.annualRevenue || null}
          onChange={(value) =>
            companyProfile.setFieldValue("annualRevenue", value ?? "")
          }
          onBlur={companyProfile.handleBlur}
          error={handleCompanyError("annualRevenue")}
          helperText={handleCompanyHelperText("annualRevenue")}
        />

        <Stack gap="var(--space-2)" sx={{ marginTop: "var(--space-5)" }}>
          <Typography variant="h6" component="h2">
            {t("Business Address")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("BusinessAddressDescription")}
          </Typography>
        </Stack>

        <ConfigureAddress
          formik={formikAddress}
          customAddressName={true}
          isDefaultLocation={true}
          ignoreCountry={true}
          autoFocus={false}
        />
      </Stack>

      <StepFooter
        primaryLabel={t("Next")}
        onPrimary={handleSubmit}
        primaryDisabled={loading}
      />
      <LoadingWithoutModal open={loading} />
    </>
  );
};

export default BusinessInformation;
